import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { selectedBlocksState, studioBlockLayout, viewPages, isReportState } from 'venn-state';

/**
 * Returns selected blocks ordered by their visual position (left to right, top to bottom)
 * for both Studio and Report Lab contexts.
 *
 * In Studio: Uses studioBlockLayout which arranges blocks in rows based on width
 * In Report Lab: Uses viewPages with grid layout positioning (x, y coordinates)
 *
 * @returns Array of block IDs ordered by visual position
 */
export const useOrderedSelectedBlocks = (): string[] => {
  const selectedBlocks = useRecoilValue(selectedBlocksState);
  const isReport = useRecoilValue(isReportState);
  const studioLayout = useRecoilValue(studioBlockLayout);
  const reportPages = useRecoilValue(viewPages);

  return useMemo(() => {
    if (selectedBlocks.length === 0) {
      return [];
    }

    // Report Lab: Order by grid position (y first, then x)
    if (isReport) {
      const blockLayoutMap = new Map<string, { x: number; y: number; page: number }>();

      reportPages.forEach((page, pageIndex) => {
        page.layout.forEach((layoutItem) => {
          blockLayoutMap.set(layoutItem.i, {
            x: layoutItem.x,
            y: layoutItem.y,
            page: pageIndex,
          });
        });
      });

      return selectedBlocks
        .filter((blockId) => blockLayoutMap.has(blockId))
        .sort((a, b) => {
          const posA = blockLayoutMap.get(a)!;
          const posB = blockLayoutMap.get(b)!;

          // First sort by page
          if (posA.page !== posB.page) {
            return posA.page - posB.page;
          }

          // Then by row (y coordinate)
          if (posA.y !== posB.y) {
            return posA.y - posB.y;
          }

          // Finally by column (x coordinate)
          return posA.x - posB.x;
        });
    }

    // Studio: Order by layout rows (top to bottom)
    const blockOrderMap = new Map<string, { row: number; position: number }>();

    studioLayout.forEach((row, rowIndex) => {
      row.forEach((blockId, positionInRow) => {
        blockOrderMap.set(blockId, {
          row: rowIndex,
          position: positionInRow,
        });
      });
    });

    return selectedBlocks
      .filter((blockId) => blockOrderMap.has(blockId))
      .sort((a, b) => {
        const posA = blockOrderMap.get(a)!;
        const posB = blockOrderMap.get(b)!;

        // Sort by row
        return posA.row - posB.row;
      });
  }, [selectedBlocks, isReport, studioLayout, reportPages]);
};
